import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';

export interface FormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'email' | 'select' | 'textarea' | 'number';
  validators?: any[];
  placeholder?: string;
  options?: { value: any; label: string }[];
  disabled?: boolean;
  required?: boolean;
}

export interface EntityEditModalData {
  title: string;
  entity: any;
  fields: FormFieldConfig[];
  submitButtonText?: string;
  cancelButtonText?: string;
}

@Component({
    selector: 'pv-entity-edit-modal',
    templateUrl: './entity-edit-modal.component.html',
    styleUrls: ['./entity-edit-modal.component.scss'],
    standalone: false
})
export class EntityEditModalComponent implements OnInit {

  public form: UntypedFormGroup;
  public isSubmitting = false;
  public data: EntityEditModalData;

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected dialogRef: NbDialogRef<EntityEditModalComponent>
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  private buildForm() {
    const formControls = {};
    
    this.data.fields.forEach(field => {
      const validators = [];
      
      if (field.required) {
        validators.push(Validators.required);
      }
      
      if (field.validators && field.validators.length > 0) {
        validators.push(...field.validators);
      }

      const initialValue = this.data.entity[field.key] || '';
      formControls[field.key] = [
        { value: initialValue, disabled: field.disabled || false },
        validators
      ];
    });

    this.form = this.formBuilder.group(formControls);
  }

  public onSubmit() {
    if (this.form.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const formValue = this.form.getRawValue();
      this.dialogRef.close({ confirmed: true, data: formValue });
    } else {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
    }
  }

  public onCancel() {
    this.dialogRef.close({ confirmed: false });
  }

  public isFieldInvalid(fieldKey: string): boolean {
    const field = this.form.get(fieldKey);
    return !!(field && field.invalid && field.touched);
  }

  public getFieldError(fieldKey: string): string {
    const field = this.form.get(fieldKey);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        const fieldConfig = this.data.fields.find(f => f.key === fieldKey);
        return `${fieldConfig?.label || fieldKey} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `Minimum length is ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `Maximum length is ${field.errors['maxlength'].requiredLength} characters`;
      }
    }
    return '';
  }
}
